import { Box, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { obj } from '../../models/common-types';
import { ConnectedData, Source } from '../../models/overview';
import { dateFormatter } from '../../utils/formatters';
import CustomIconsList from '../CustomIconsList/CustomIconsList';
import { getPlatformImage, getSearchTypeFromSource } from '../../utils/utils';

interface UniqueSource {
    sources?: any;
    providers?: any;
    lastSeenArr?: any;
    lastVerifiedArr?: any;
    validSinceArr?: any;
    searchInputArr?: any;
    searchIdentifierArr?: any;
    connectedData?: ConnectedData[];
}

interface UniqueSourcesIconListProps {
    originalSources: Source[];
    listShouldWrap?: boolean;
    displayUnexpanded?: number;
}

const UniqueSourcesIconList: FC<UniqueSourcesIconListProps> = ({
    originalSources,
    listShouldWrap,
    displayUnexpanded,
}) => {
    const [sources, setSources] = useState<obj<UniqueSource>>({});

    useEffect(() => {
        const tempSources: obj<UniqueSource> = {};

        if (originalSources) {
            originalSources
                .sort()
                .filter((src) => src.source !== undefined)
                .forEach((originalSource) => {
                    const key = originalSource.site || originalSource.source;
                    if (tempSources[key]) {
                        tempSources[key].providers.push(originalSource.provider);
                        tempSources[key].sources.push(originalSource.source);
                        tempSources[key].lastSeenArr.push(originalSource.lastSeen);
                        tempSources[key].validSinceArr.push(originalSource.validSince);
                        tempSources[key].lastVerifiedArr.push(originalSource.lastVerified);
                        tempSources[key].searchInputArr.push(originalSource.searchInfo);
                        tempSources[key].searchIdentifierArr.push(originalSource.searchIdentifier);
                        tempSources[key].connectedData.push(originalSource.connectedData);
                    } else if (key) {
                        tempSources[key] = {
                            providers: [originalSource.provider],
                            sources: [originalSource.source],
                            lastSeenArr: [originalSource.lastSeen],
                            validSinceArr: [originalSource.validSince],
                            lastVerifiedArr: [originalSource.lastVerified],
                            searchInputArr: [originalSource.searchInfo],
                            searchIdentifierArr: [originalSource.searchIdentifier],
                            connectedData: [originalSource.connectedData],
                        };
                    }
                });
        }

        setSources(tempSources);
    }, [originalSources]);

    return (
        <CustomIconsList
            disableExpansion={true}
            displayUnexpanded={displayUnexpanded || 10}
            listShouldWrap={listShouldWrap}
            list={Object.entries(sources).map(([platform, item]) => {
                const isIconCrossed = item.connectedData?.some(
                    (data) => data?.type === 'CHECKER' && data?.description?.includes('NOT_'),
                );

                return {
                    iconName: platform,
                    isIconCrossed,
                    altSourceNames: item.providers,
                    tooltipContent: (
                        <Box>
                            {item.providers?.map((provider, index) => {
                                const searchType = getSearchTypeFromSource(item.searchInputArr?.[index]);
                                const searchIdentifier = item.searchIdentifierArr?.[index];

                                const connectedDataMessage = item.connectedData?.[index]?.description;
                                const showConnectedData =
                                    item.connectedData?.[index]?.type === 'CHECKER' && connectedDataMessage;
                                return (
                                    <Box key={index} sx={{ mt: index ? 2 : 0 }}>
                                        <Typography variant="s16w500">{platform}</Typography>
                                        {showConnectedData && (
                                            <Box>
                                                <Typography variant="s16w500" className="color-md">
                                                    {i18n.translate(`SOURCE_SEARCHTERM_CHECK.${connectedDataMessage}`)}
                                                </Typography>
                                            </Box>
                                        )}
                                        {searchType && (
                                            <Box>
                                                <Typography variant="s16w400" className="color-md">
                                                    {searchType}
                                                </Typography>
                                            </Box>
                                        )}
                                        {searchIdentifier && (
                                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                <Typography
                                                    variant="s16w400"
                                                    className="color-md"
                                                    sx={{ textWrap: 'nowrap' }}
                                                >
                                                    {i18n.translate('Search Term')}:
                                                </Typography>
                                                <Typography
                                                    variant="s16w400"
                                                    sx={{
                                                        ml: 1,
                                                        mr: 1,
                                                        textWrap: 'nowrap',
                                                        maxWidth: 100,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                    title={searchIdentifier.identifier}
                                                >
                                                    {searchIdentifier.identifier}
                                                </Typography>
                                                <img
                                                    width="16"
                                                    height="16"
                                                    src={getPlatformImage(searchIdentifier.network || provider)}
                                                    alt=""
                                                />
                                            </Box>
                                        )}
                                        {platform !== item.sources?.[index] && (
                                            <Box sx={{ display: 'flex' }}>
                                                <Typography variant="s16w400" className="color-md">
                                                    {i18n.translate('Source')}:
                                                </Typography>
                                                <Typography variant="s16w400" sx={{ ml: 1 }}>
                                                    {item.sources?.[index]}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex' }}>
                                            <Typography variant="s16w400" className="color-md">
                                                {i18n.translate('Provider')}:
                                            </Typography>
                                            <Typography variant="s16w400" sx={{ ml: 1 }}>
                                                {provider}
                                            </Typography>
                                        </Box>
                                        {item.lastSeenArr?.[index] && (
                                            <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                                <Typography variant="s16w400" className="color-md">
                                                    {i18n.translate('Last Seen')}:
                                                </Typography>
                                                <Typography variant="s16w400" sx={{ ml: 1 }}>
                                                    {dateFormatter(item.lastSeenArr?.[index])}
                                                </Typography>
                                            </Box>
                                        )}
                                        {item.validSinceArr?.[index] && (
                                            <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                                <Typography variant="s16w400" className="color-md">
                                                    {i18n.translate('Valid Since')}:
                                                </Typography>
                                                <Typography variant="s16w400" sx={{ ml: 1 }}>
                                                    {dateFormatter(item.validSinceArr?.[index])}
                                                </Typography>
                                            </Box>
                                        )}
                                        {item.lastVerifiedArr?.[index] && (
                                            <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                                                <Typography variant="s16w400" className="color-md">
                                                    {i18n.translate('Last Verified')}:
                                                </Typography>
                                                <Typography variant="s16w400" sx={{ ml: 1 }}>
                                                    {dateFormatter(item.lastVerifiedArr?.[index])}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ),
                };
            })}
        />
    );
};

export default UniqueSourcesIconList;
